import { BatchQueryResult, open, OPSQLiteConnection } from '@op-engineering/op-sqlite';

export type { BatchQueryResult };
/** 批量执行元组；Web 上 op-sqlite 包可能不导出此类型，与 shim 及原生约定一致 */
export type SQLBatchTuple = [string, any[]];

import log from '../log';
import { current_clear_sql, current_init_sql, current_version } from './sqls/current';
import { update_paths } from './sqls/update_paths';

const LogName = '[KKDB]';
const debug_enabled = false;

export type Result = {
	error: any;
};

export const ResultOK: Result = {
	error: null,
};

export type QueryResult = {
	error: any;
	data?: any[] | null;
};

export const ResultDumplicateOpen: QueryResult = {
	error: 'DumplicateOpen',
};

export const ResultNoOpen: QueryResult = {
	error: 'NoOpen',
};

export type ExecuteResult = {
	error: any;
	rowsAffected: number;
	insertId: number;
};

export const ExecuteResultNoOpen: ExecuteResult = {
	error: 'NoOpen',
	rowsAffected: 0,
	insertId: 0,
};

export class DB {
	private fileName: string;
	private is_open: boolean;
	private db: OPSQLiteConnection | null;
	public constructor(fileName: string) {
		this.fileName = fileName;
		this.is_open = false;
		this.db = null;
	}

	public async Open(checkver: boolean = true): Promise<Result> {
		const r1 = await this.open();
		if (r1.error) {
			return r1;
		}
		// const r2 = await this.check();
		// if (r2.error) {
		//     return r2;
		// }
		if (checkver) {
			const r3 = await this.update_version();
			if (r3.error) {
				await this.close();
				return r3;
			}
		}
		return ResultOK;
	}

	public async Close(): Promise<Result> {
		return await this.close();
	}

	public Query(sqlstr: string, params: any[]): Promise<QueryResult> {
		return this.query(sqlstr, params);
	}

	public Execute(sqlstr: string, params: any[]): Promise<ExecuteResult> {
		return this.execute(sqlstr, params);
	}
	public BatchExecute(commands: SQLBatchTuple[]): Promise<BatchQueryResult> {
		return this.batchExecute(commands);
	}

	// 删除数据库
	public static async DropDatabase(dbname: string) {
		// const db = await this.db.
	}

	// 打开数据库
	private async open(): Promise<Result> {
		// 如果已经打开直接返回成功
		if (this.is_open) {
			return ResultDumplicateOpen;
		}
		// 真正执行打开
		try {
			this.db = await open({ name: this.fileName });
			this.is_open = true;
			return ResultOK;
		} catch (err) {
			this.is_open = false;
			log.error(LogName, 'open', this.fileName, 'exception', err);
			return { error: err };
		}
	}

	// 关闭库
	private close(): Promise<Result> {
		return new Promise<Result>((resolve) => {
			try {
				if (this.db == null || !this.is_open) {
					resolve(ResultOK);
					return;
				}
				this.is_open = false;
				this.db.close();
				this.db = null;
				log.info(LogName, 'close', this.fileName, 'success');
				resolve(ResultOK);
			} catch (err) {
				log.trace(LogName, 'close', this.fileName, 'exception', err);
				resolve({ error: err });
			}
		});
	}

    private async update_version(): Promise<Result> {
        const r1 = await this.check_table_exists("t_version");
        if (r1.error) {
            return { error: r1 };
        }

        // 不存在直接执行初始化sql
        if (!r1.exists) {
            log.info("t_version表不存在准备重建库表");
            // 可能有其它异常导致，先清理再添加
            const r6 = await this.batch_execute(current_clear_sql);
            if (r6.error) {
                return { error: r6.error };
            }
            const r2 = await this.batch_execute(current_init_sql);
            if (r2.error) {
                return { error: r2.error };
            }
            return { error: null };
        }

        // 存在则查询下版本号
        const r3 = await this.query("select val from t_version where key='db_version'", []);

        if (r3.error) {
            return { error: r3.error };
        }

        debug_enabled && log.debug("version data", r3.data);

        let ver = -1;
        if (r3.data && r3.data.length > 0) {
            const row = r3.data[0];
            if (row && row.val) {
                ver = parseInt(row.val, 10);
            }
        }

        // 异常数据数据库进行清理
        if (ver <= 0 || ver > current_version) {
            log.warn("出现异常版本,清理重建数据库 ver:", ver);
            const r4 = await this.batch_execute(current_clear_sql);
            if (r4.error) {
                return { error: r4.error };
            }
            const r5 = await this.batch_execute(current_init_sql);
            if (r5.error) {
                return { error: r5.error };
            }
            return { error: null };
        }

        log.info("t_version表存在，且当前版本为", ver, "检查是否有升级脚本");
        // 根据升级路径挨个执行sql
        let update_cnt = 0;
        for (let i = 0; i < update_paths.length; i++) {
            let version = update_paths[i][0];
            let versionNum = 0;
            if (Array.isArray(version) && version.length === 1 && typeof version[0] === "string") {
                versionNum = Number(version[0]);
            } else if (typeof version === "number") {
                versionNum = version;
            }
            if (
                versionNum > ver &&
                versionNum <= current_version
            ) {
                log.info("准备进行升级到", version);
                const sqls = update_paths[i][1] as string[];
                const r6 = await this.batch_execute(sqls);
                if (r6.error) {
                    log.warn("升级到", version, "失败", r6.error);
                    return { error: r6.error };
                }
                log.info("升级到", version, "完成");
                update_cnt++;
            }
        }
        log.info("检查完成,执行了", update_cnt, "个版本的升级脚本");
        const rRepair = await this.repair_legacy_schema();
        if (rRepair.error) {
            log.warn(LogName, "repair_legacy_schema 失败", rRepair.error);
            return rRepair;
        }
        return ResultOK;
    }

    /**
     * 幂等补齐旧库缺失列（迁移未执行、热更新版本号与 DDL 不一致、Web 仅刷新 JS 等）。
     * 与 imsdk/db/t_user.ts user_upsert、t_topic、t_group 当前 DDL 一致。
     */
    private async repair_legacy_schema(): Promise<Result> {
        const tUser = await this.check_table_exists("t_user");
        if (tUser.error) {
            return { error: tUser.error };
        }
        if (tUser.exists) {
            const pi = await this.query("PRAGMA table_info(t_user)", []);
            if (pi.error) {
                return { error: pi.error };
            }
            const cols = new Set<string>();
            for (const row of pi.data || []) {
                if (row && (row as { name?: string }).name != null) {
                    cols.add(String((row as { name: string }).name).toLowerCase());
                }
            }
            const userAdds: { key: string; sql: string }[] = [
                { key: "sex", sql: "ALTER TABLE t_user ADD COLUMN sex TEXT DEFAULT ''" },
                { key: "birth", sql: "ALTER TABLE t_user ADD COLUMN birth TEXT DEFAULT ''" },
                { key: "privacy", sql: "ALTER TABLE t_user ADD COLUMN privacy TEXT DEFAULT ''" },
                { key: "present", sql: "ALTER TABLE t_user ADD COLUMN present TEXT DEFAULT ''" },
                { key: "titles", sql: "ALTER TABLE t_user ADD COLUMN titles TEXT DEFAULT ''" },
                { key: "rtel", sql: "ALTER TABLE t_user ADD COLUMN rtel TEXT DEFAULT ''" },
                { key: "rtelcheck", sql: "ALTER TABLE t_user ADD COLUMN rtelCheck TEXT DEFAULT ''" },
                { key: "remail", sql: "ALTER TABLE t_user ADD COLUMN remail TEXT DEFAULT ''" },
                { key: "remailcheck", sql: "ALTER TABLE t_user ADD COLUMN remailCheck TEXT DEFAULT ''" },
                { key: "qrcode", sql: "ALTER TABLE t_user ADD COLUMN qrcode TEXT DEFAULT ''" },
                { key: "qrcode_desc", sql: "ALTER TABLE t_user ADD COLUMN qrcode_desc TEXT DEFAULT ''" },
            ];
            for (const { key, sql } of userAdds) {
                if (!cols.has(key)) {
                    log.info(LogName, "repair_legacy_schema: ALTER t_user 补列", key);
                    const e = await this.execute(sql, []);
                    if (e.error) {
                        return { error: e.error };
                    }
                    cols.add(key);
                }
            }
        }

        const tTopic = await this.check_table_exists("t_topic");
        if (tTopic.error) {
            return { error: tTopic.error };
        }
        if (tTopic.exists) {
            const pi = await this.query("PRAGMA table_info(t_topic)", []);
            if (pi.error) {
                return { error: pi.error };
            }
            const cols = new Set<string>();
            for (const row of pi.data || []) {
                if (row && (row as { name?: string }).name != null) {
                    cols.add(String((row as { name: string }).name).toLowerCase());
                }
            }
            if (!cols.has("unread")) {
                log.info(LogName, "repair_legacy_schema: ALTER t_topic 补列 unread");
                const e = await this.execute(
                    "ALTER TABLE t_topic ADD COLUMN unread INTEGER DEFAULT 0",
                    [],
                );
                if (e.error) {
                    return { error: e.error };
                }
            }
            const topicAdds: { key: string; sql: string }[] = [
                { key: "msg_synced", sql: "ALTER TABLE t_topic ADD COLUMN msg_synced INTEGER DEFAULT 0" },
                { key: "bridge_state", sql: "ALTER TABLE t_topic ADD COLUMN bridge_state INTEGER DEFAULT 0" },
                { key: "bridge_cursor", sql: "ALTER TABLE t_topic ADD COLUMN bridge_cursor TEXT DEFAULT ''" },
                { key: "bridge_pages", sql: "ALTER TABLE t_topic ADD COLUMN bridge_pages INTEGER DEFAULT 0" },
                { key: "bridge_epoch", sql: "ALTER TABLE t_topic ADD COLUMN bridge_epoch INTEGER DEFAULT 0" },
                { key: "bridge_updated_at", sql: "ALTER TABLE t_topic ADD COLUMN bridge_updated_at INTEGER DEFAULT 0" },
            ];
            for (const { key, sql } of topicAdds) {
                if (!cols.has(key)) {
                    log.info(LogName, "repair_legacy_schema: ALTER t_topic 补列", key);
                    const e = await this.execute(sql, []);
                    if (e.error) {
                        return { error: e.error };
                    }
                    cols.add(key);
                }
            }
        }

        const tGroup = await this.check_table_exists("t_group");
        if (tGroup.error) {
            return { error: tGroup.error };
        }
        if (tGroup.exists) {
            const pi = await this.query("PRAGMA table_info(t_group)", []);
            if (pi.error) {
                return { error: pi.error };
            }
            const cols = new Set<string>();
            for (const row of pi.data || []) {
                if (row && (row as { name?: string }).name != null) {
                    cols.add(String((row as { name: string }).name).toLowerCase());
                }
            }
            // 与 sqls/current.ts CREATE t_group、t_group.ts upsert 对齐（update_sql5 旧表缺大量列）
            const groupAdds: { key: string; sql: string }[] = [
                { key: "notice_read", sql: "ALTER TABLE t_group ADD COLUMN notice_read TEXT DEFAULT ''" },
                { key: "avatar", sql: "ALTER TABLE t_group ADD COLUMN avatar TEXT DEFAULT ''" },
                { key: "scene", sql: "ALTER TABLE t_group ADD COLUMN scene INTEGER DEFAULT 0" },
                { key: "sceneid", sql: "ALTER TABLE t_group ADD COLUMN sceneid TEXT DEFAULT ''" },
                { key: "flag", sql: "ALTER TABLE t_group ADD COLUMN flag INTEGER DEFAULT 0" },
                { key: "qrcode", sql: "ALTER TABLE t_group ADD COLUMN qrcode TEXT DEFAULT ''" },
                { key: "qrcode_desc", sql: "ALTER TABLE t_group ADD COLUMN qrcode_desc TEXT DEFAULT ''" },
                {
                    key: "group_check_switch",
                    sql: "ALTER TABLE t_group ADD COLUMN group_check_switch TEXT DEFAULT ''",
                },
                {
                    key: "group_allow_member_list",
                    sql: "ALTER TABLE t_group ADD COLUMN group_allow_member_list TEXT DEFAULT '1'",
                },
                {
                    key: "group_allow_member_visit",
                    sql: "ALTER TABLE t_group ADD COLUMN group_allow_member_visit TEXT DEFAULT '1'",
                },
                {
                    key: "group_friend_invite_enter",
                    sql: "ALTER TABLE t_group ADD COLUMN group_friend_invite_enter TEXT DEFAULT '1'",
                },
                { key: "group_ban_chat", sql: "ALTER TABLE t_group ADD COLUMN group_ban_chat TEXT DEFAULT ''" },
                {
                    key: "group_chat_interval_time",
                    sql: "ALTER TABLE t_group ADD COLUMN group_chat_interval_time TEXT DEFAULT ''",
                },
                { key: "remark", sql: "ALTER TABLE t_group ADD COLUMN remark TEXT DEFAULT ''" },
                { key: "group_sn", sql: "ALTER TABLE t_group ADD COLUMN group_sn TEXT DEFAULT ''" },
                { key: "intro", sql: "ALTER TABLE t_group ADD COLUMN intro TEXT DEFAULT ''" },
                {
                    key: "group_member_alias",
                    sql: "ALTER TABLE t_group ADD COLUMN group_member_alias TEXT DEFAULT ''",
                },
                {
                    key: "group_member_image",
                    sql: "ALTER TABLE t_group ADD COLUMN group_member_image TEXT DEFAULT ''",
                },
                {
                    key: "group_member_video",
                    sql: "ALTER TABLE t_group ADD COLUMN group_member_video TEXT DEFAULT ''",
                },
                {
                    key: "group_member_doc",
                    sql: "ALTER TABLE t_group ADD COLUMN group_member_doc TEXT DEFAULT ''",
                },
                {
                    key: "group_member_quit_msg",
                    sql: "ALTER TABLE t_group ADD COLUMN group_member_quit_msg TEXT DEFAULT ''",
                },
                { key: "join_notice", sql: "ALTER TABLE t_group ADD COLUMN join_notice INTEGER DEFAULT 0" },
                {
                    key: "group_admin_special",
                    sql: "ALTER TABLE t_group ADD COLUMN group_admin_special TEXT DEFAULT ''",
                },
                { key: "member_count", sql: "ALTER TABLE t_group ADD COLUMN member_count INTEGER DEFAULT 0" },
                {
                    key: "member_synced",
                    sql: "ALTER TABLE t_group ADD COLUMN member_synced INTEGER DEFAULT 0",
                },
            ];
            for (const { key, sql } of groupAdds) {
                if (!cols.has(key)) {
                    log.info(LogName, "repair_legacy_schema: ALTER t_group 补列", key);
                    const e = await this.execute(sql, []);
                    if (e.error) {
                        return { error: e.error };
                    }
                    cols.add(key);
                }
            }
        }

        // t_auth: 补齐历史遗漏列（初始建表 SQL 未包含，新用户全新安装会导致 insertAuth 报错失败）
        const tAuth = await this.check_table_exists("t_auth");
        if (!tAuth.error && tAuth.exists) {
            const pi = await this.query("PRAGMA table_info(t_auth)", []);
            if (!pi.error) {
                const authCols = new Set<string>();
                for (const row of pi.data || []) {
                    if (row && (row as { name?: string }).name != null) {
                        authCols.add(String((row as { name: string }).name).toLowerCase());
                    }
                }
                const authAdds: { key: string; sql: string }[] = [
                    { key: "switch_regist",   sql: "ALTER TABLE t_auth ADD COLUMN switch_regist INTEGER DEFAULT 0" },
                    { key: "switch_tab_main", sql: "ALTER TABLE t_auth ADD COLUMN switch_tab_main INTEGER DEFAULT 0" },
                    { key: "switch_tab_mine", sql: "ALTER TABLE t_auth ADD COLUMN switch_tab_mine INTEGER DEFAULT 1" },
                    { key: "switch_rtc",      sql: "ALTER TABLE t_auth ADD COLUMN switch_rtc INTEGER DEFAULT 0" },
                ];
                for (const { key, sql } of authAdds) {
                    if (!authCols.has(key)) {
                        log.info(LogName, "repair_legacy_schema: ALTER t_auth 补列", key);
                        const e = await this.execute(sql, []);
                        if (e.error) {
                            return { error: e.error };
                        }
                        authCols.add(key);
                    }
                }
            }
        }

        return ResultOK;
    }

    // 检查表是否存在
    private async check_table_exists(
        table_name: string
    ): Promise<{ exists: boolean; error: any }> {
        const sqlstr =
            "select 1 from sqlite_master where type = 'table' and name = ?";
        const params = [table_name];
        const result = await this.query(sqlstr, params);
        if (result.error) {
            return {
                exists: false,
                error: result.error
            };
        }
        if (result.data) {
            return {
                exists: result.data.length > 0,
                error: null
            };
        }
        return {
            exists: false,
            error: null
        };
    }

    // 执行数据库的查询操作
    // private query(
    //     sqlstr: string,
    //     params: any[]
    // ): Promise<QueryResult> {
    //     return new Promise((resolve) => {
    //         let resolve_cnt = 0;
    //         if (this.db == null || !this.is_open) {
    //             resolve_cnt++;
    //             log.warn(
    //                 LogName,
    //                 `QUERY FAIL:${resolve_cnt} SQL: ${sqlstr} PARAMS: ${params} ERR: DBNoOpen`
    //             );
    //             resolve(ResultNoOpen);
    //             return;
    //         }

    //         // debug_enabled && log.debug(LogName, 'QUERY', {sqlstr, params});
    //         sqlstr = fix_sql_str(sqlstr);
    //         const watch = time.watch();

    //         this.db.transaction(async (tx) => {
    //             try {
    //                 const result = await tx.executeAsync(sqlstr, params)
    //                 const rows = [];
    //                 if (result.rows) {
    //                     for (let i = 0; i < result.rows.length; i++) {
    //                         rows.push(result.rows.item(i));
    //                     }
    //                 }
    //                 resolve_cnt++;
    //                 debug_enabled &&
    //                     log.debug(
    //                         LogName,
    //                         `${watch()}ms`,
    //                         `QUERY SUCC:${resolve_cnt} ROWS:${result.rows?.length}`,
    //                         { sqlstr, params },
    //                     );
    //                 resolve({ error: null, data: rows });
    //             } catch (err) {
    //                 resolve_cnt++;
    //                 if (err === undefined) {
    //                     // 很奇怪 错误的时候这里会call 但是没有错误
    //                 } else {
    //                     log.warn(
    //                         LogName,
    //                         `${watch()}ms`,
    //                         `QUERY ERROR:${resolve_cnt}`,
    //                         err,
    //                         { sqlstr, params }
    //                     );
    //                     const error = `QUERY ERROR:${resolve_cnt}-${err}-${sqlstr}}`
    //                     resolve({
    //                         error: error
    //                     });
    //                 }

    //             }
    //         });

    //     })
    // } // END query
    private async query(sqlstr: string, params: any[]): Promise<QueryResult> {
        if (!this.db || !this.is_open) return ResultNoOpen;

        sqlstr = fix_sql_str(sqlstr);
        try {
            const rows: any[] = [];
            const result = await this.db.executeAsync(sqlstr, params);
            if (result.rows) {
                for (let i = 0; i < result.rows.length; i++) {
                    rows.push(result.rows.item(i));
                }
            }
            return { error: null, data: rows };
        } catch (err) {
            log.warn(LogName, "QUERY ERROR", err, { sqlstr, params });
            return { error: err };
        }
    }

    //
    // private execute(
    //     sqlstr: string,
    //     params: any[]
    // ): Promise<ExecuteResult> {
    //     return new Promise((resolve) => {
    //         sqlstr = fix_sql_str(sqlstr);
    //         let resolve_cnt = 0;
    //         if (this.db == null || !this.is_open) {
    //             resolve_cnt++;
    //             log.warn(
    //                 LogName,
    //                 resolve_cnt,
    //                 `execute fail! sql: ${sqlstr} params: ${params} err: db no open`
    //             );
    //             resolve(ExecuteResultNoOpen);
    //             return;
    //         }
    //         const watch = time.watch();

    //         this.db.transaction(async (tx) => {
    //             try {
    //                 const result = await tx.executeAsync(sqlstr, params)
    //                 resolve_cnt++;
    //                 debug_enabled &&
    //                     log.debug(
    //                         LogName,
    //                         `${watch()}ms`,
    //                         `EXEC SUCC:${resolve_cnt} AFFECTED:${result.rowsAffected} INSERTID:${result.insertId}`,
    //                         { sqlstr }
    //                     );
    //                 resolve({
    //                     error: null,
    //                     rowsAffected: result.rowsAffected,
    //                     insertId: result.insertId || -1
    //                 })

    //             } catch (err) {
    //                 resolve_cnt++;
    //                 if (err === undefined) {
    //                     // 很奇怪 错误的时候这里会call 但是没有错误
    //                 } else {
    //                     log.warn(
    //                         LogName,
    //                         `${watch()}ms`,
    //                         `EXEC FAIL:${resolve_cnt}`,
    //                         err,
    //                         { sqlstr, params }
    //                     );
    //                     const error = `EXEC FAIL:${resolve_cnt}-${err}-${sqlstr}}`
    //                     resolve({
    //                         error: error,
    //                         rowsAffected: 0,
    //                         insertId: 0
    //                     });
    //                 }
    //             }
    //         });
    //     });
    // }

    private async execute(sqlstr: string, params: any[]): Promise<ExecuteResult> {
        if (!this.db || !this.is_open) return ExecuteResultNoOpen;

        sqlstr = fix_sql_str(sqlstr);
        try {
            let rowsAffected = 0;
            let insertId = -1;

            const result = await this.db.executeAsync(sqlstr, params);
            rowsAffected = result.rowsAffected || 0;
            insertId = result.insertId || -1;
            return { error: null, rowsAffected, insertId };
        } catch (err) {
            log.warn(LogName, "EXEC FAIL", err, { sqlstr, params });
            return { error: err, rowsAffected: 0, insertId: 0 };
        }
    }

    // 批量执行
    private async batchExecute(commands: SQLBatchTuple[]): Promise<BatchQueryResult> {

        if (this.db == null || !this.is_open) {
            return { rowsAffected: 0 };
        }
        try {
            return await this.db.executeBatchAsync(commands)
        } catch (err) {
            log.error("batch failed", err);
            return { rowsAffected: 0 };
        }

    }


    // 批量执行
    private async batch_execute(sqls: string[]): Promise<Result> {
        if (!this.db || !this.is_open) return ExecuteResultNoOpen;

        const commands: SQLBatchTuple[] = sqls.map((sql) => [sql, [] as any[]]);

        try {
            await this.db.executeBatchAsync(commands);
            return ResultOK;
        } catch (err) {
            return { error: err };
        }

    }

    //
    // // 检查表字段是否一样
    // private async check(): Promise<Result> {
    //     const n = databaseDef.Tables.length;
    //     for (let i = 0; i < n; i++) {
    //         const t = databaseDef.Tables[i];
    //         const r = await this.check_and_fix_table(t.Name);
    //         if (r.error) {
    //             return r;
    //         }
    //     }
    //     return ResultOK;
    // }
    //
    // // 初始化数据库
    // // 1. 如果库表不存在则创建
    // // 2. 如果表字段不存在则创建
    // // 3. 如果表字段存在但是定义里没有则删除
    // private async check_and_fix_table(table_name: string): Promise<Result> {
    //     const t1 = await this.check_table_exists(table_name);
    //     if (t1.error) {
    //         return {error: t1.error};
    //     }
    //
    //     // 检查表定义是否存在
    //     const tableDef = getTableDef(table_name);
    //     if (!tableDef) {
    //         return {error: `TableDefineNotFound:${table_name}`};
    //     }
    //     if (tableDef.Columns.length <= 0) {
    //         return {error: `TableColumnsDefineEmpty:${table_name}`};
    //     }
    //
    //     // 表不存在，创建表
    //     if (!t1.exists) {
    //         return await this.create_table(tableDef);
    //     }
    //
    //     // 表存在，检查表字段
    //     const t3 = await this.get_table_colum_nams(table_name);
    //     if (t3.error) {
    //         return {error: t3.error};
    //     }
    //
    //     return ResultOK;
    // }
    //
    // // 获取当前数据表里的列名
    // private async get_table_colum_nams(table_name: string): Promise<{
    //     error: any;
    //     columnNames: string[];
    // }> {
    //     const sqlstr = `PRAGMA table_info([${table_name}])`;
    //     const result = await this.query(sqlstr, undefined);
    //     if (result.error) {
    //         return {error: result.error, columnNames: []};
    //     }
    //     if (!result.data || result.data.length <= 0) {
    //         return {error: null, columnNames: []};
    //     }
    //     const cols: string[] = [];
    //     const n = result.data.length;
    //     for (let i = 0; i < n; i++) {
    //         const c = result.data[i];
    //         if (c && c.name) {
    //             cols.push(c.name);
    //         }
    //     }
    //     return {error: null, columnNames: cols};
    // }
    //
    // // 创建表
    // private async create_table(tableDef: TableDef): Promise<Result> {
    //     let cols = '';
    //     const n = tableDef.Columns.length;
    //     for (let i = 0; i < n; i++) {
    //         const c = tableDef.Columns[i];
    //         if (c.Name.length <= 0) {
    //             return {error: `TableColumnNameEmpty:${tableDef.Name}:${i}`};
    //         }
    //         let d = c.Name;
    //         if (c.Type && c.Type.length > 0) {
    //             d += ' ' + c.Type;
    //         }
    //         if (c.Primary && c.Primary === true) {
    //             d += ' PRIMARY KEY';
    //         }
    //         if (c.NotNull && c.NotNull === true) {
    //             d += ' NOT NULL';
    //         }
    //
    //         if (i === 0) {
    //             cols += '\n' + d;
    //         } else {
    //             cols += ',\n' + d;
    //         }
    //     }
    //
    //     let sqlstr = `create table ${tableDef.Name} (${cols})`;
    //     const result = await this.execute(sqlstr, undefined);
    //     return {error: result.error};
    // }
    //
    // // 添加表字段
    // private async add_column(
    //     table_name: string,
    //     columnDef: ColumnDef,
    // ): Promise<Result> {
    //     const sqlstr = `ALTER TABLE ${table_name} ADD COLUMN ${columnDef.Name} ${columnDef.Type};`;
    //     const result = await this.execute(sqlstr, undefined);
    //     return {error: result.error};
    // }
    //
    // // 删除表字段
    // private async remove_column(
    //     table_name: string,
    //     column_name: string,
    // ): Promise<Result> {
    //     const sqlstr = `ALTER TABLE ${table_name} DROP COLUMN ${column_name}`;
    //     const result = await this.execute(sqlstr, undefined);
    //     return {error: result.error};
    // }
    //
    // // 删除表
    // private async drop_table(table_name: string): Promise<Result> {
    //     log.warn(LogName, 'will drop table', table_name);
    //     const sqlstr = `drop table ${table_name};`;
    //     const result = await this.execute(sqlstr, undefined);
    //     return {error: result.error};
    // }
}

// ----- 废弃代码 -----

//
// type ColumnDef = {
//     Name: string; // 必填
//     Type?: string; // 数据库类型
//     Primary?: boolean; // 是否为主键 只能设定一个主键
//     NotNull?: boolean; //
// };
//
// type TableDef = {
//     Name: string; // 必填
//     Columns: ColumnDef[];
// };
//
// type DatabaseDef = {
//     Version: string; // 必填
//     Tables: TableDef[]; // 可空
// };
//
// // 库表定义
// const databaseDef: DatabaseDef = {
//     Version: '0.0.1', // 当前定义的版本号，为了方便做版本管理，割接，上报日志使用
//     Tables: [
//         {
//             Name: 't_app', // 表名
//             Columns: [
//                 {Name: 'appkey', Primary: true, NotNull: true},
//                 {Name: 'main_appkey'},
//                 {Name: 'name'},
//                 {Name: 'desc'},
//             ], // 字段定义
//         },
//     ],
// };
//
// function getTableDef(table_name: string): TableDef | null {
//     const n = databaseDef.Tables.length;
//     for (let i = 0; i < n; i++) {
//         const tableDef = databaseDef.Tables[i];
//         if (tableDef.Name === table_name) {
//             return tableDef;
//         }
//     }
//     return null;
// }

function fix_sql_str(sqlstr: string): string {
    sqlstr.trim();
    if (sqlstr.endsWith(";")) {
        sqlstr = sqlstr.replace(/;$/, "");
    }
    return sqlstr;
}
